import { ApiClient, type AuthTokens, type PagedList } from '@clientma/core';

/**
 * The provider console's view of the API.
 *
 * Every path is under `/api/v1/provider`, and every one of them reads across tenants — which is
 * exactly why this client is separate from the tenant one. A tenant's client is scoped by the
 * tenant its token names; this one is scoped by an operator claim the server checks on every
 * call. Keeping them apart means neither can be mistaken for the other.
 */

export interface ProviderOperator {
  userId: string;
  email: string;
  displayName: string;
  locale: string;
  timeZoneId: string;
}

/**
 * A subscription and what is about to happen to it.
 *
 * The clock renews these on its own; this is how somebody here sees what it is about to do, and
 * which customers are not paying for what it already did.
 */
export interface ProviderRenewalRow {
  subscriptionId: string;
  tenantId: string;
  tenantName: string;
  planName?: string | null;
  status: string;
  interval: string;
  seats: number;
  currency: string;
  termAmount: number;
  renewsAtUtc: string;
  daysUntilRenewal: number;
  cancelAtPeriodEnd: boolean;
  gracePeriodEndsAtUtc?: string | null;
  billingEmail?: string | null;
  unpaidInvoiceNumber?: string | null;
  unpaidAmount?: number | null;
  unpaidIsOverdue: boolean;
}

// ── Plans
//
// The catalogue as the people who sell it maintain it. A price is added rather than edited:
// a subscription records the exact price row it was sold on, so changing one in place would
// re-price existing customers at their next renewal without anybody deciding to.

export interface ProviderPlanRow {
  id: string;
  planKey: string;
  name: string;
  tier: string;
  applicationKey?: string | null;
  /** AI credits the plan includes each month. Null or absent when it includes none. */
  aiCreditsPerMonth?: number | null;
  isActive: boolean;
  isPubliclyListed: boolean;
  isBundle: boolean;
  isAddOn: boolean;
  includedSeats: number;
  maximumSeats?: number | null;
  isTrialAvailable: boolean;
  trialDays: number;
  displayOrder: number;
  priceCount: number;
  entitlementCount: number;
  subscriberCount: number;
}

export interface ProviderPlanPriceRow {
  id: string;
  currency: string;
  interval: string;
  baseAmount: number;
  perSeatAmount: number;
  setupFee: number;
  taxBehavior: string;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  isActive: boolean;
  isInForce: boolean;
  /** Sold on at least once, so it can no longer be corrected in place. */
  isInUse: boolean;
}

export interface ProviderPlanEntitlementRow {
  id: string;
  applicationKey: string;
  featureKey: string;
  displayName: string;
  isEnabled: boolean;
  limitValue?: number | null;
  resetInterval: string;
  overageBehavior: string;
  overageUnitPrice?: number | null;
}

export interface ProviderPlanDetail {
  id: string;
  planKey: string;
  name: string;
  description?: string | null;
  tier: string;
  applicationKey?: string | null;
  bundledApplicationKeys?: string | null;
  isBundle: boolean;
  isAddOn: boolean;
  isPubliclyListed: boolean;
  isActive: boolean;
  displayOrder: number;
  isTrialAvailable: boolean;
  trialDays: number;
  trialSeatCap?: number | null;
  trialRequiresPaymentMethod: boolean;
  includedSeats: number;
  maximumSeats?: number | null;
  highlightsJson?: string | null;
  subscriberCount: number;
  prices: ProviderPlanPriceRow[];
  entitlements: ProviderPlanEntitlementRow[];
}

export interface ProviderPlanInput {
  planKey: string;
  name: string;
  description?: string | null;
  tier: string;
  applicationKey?: string | null;
  bundledApplicationKeys?: string | null;
  isBundle: boolean;
  isAddOn: boolean;
  isPubliclyListed: boolean;
  displayOrder: number;
  isTrialAvailable: boolean;
  trialDays: number;
  trialSeatCap?: number | null;
  trialRequiresPaymentMethod: boolean;
  includedSeats: number;
  maximumSeats?: number | null;
  highlightsJson?: string | null;
}

export interface ProviderPlanPriceInput {
  currency: string;
  interval: string;
  baseAmount: number;
  perSeatAmount: number;
  setupFee: number;
  taxBehavior: string;
  effectiveFromUtc?: string | null;
  effectiveUntilUtc?: string | null;
}

export interface ProviderPlanEntitlementInput {
  featureKey: string;
  displayName: string;
  applicationKey: string;
  isEnabled: boolean;
  limitValue?: number | null;
  resetInterval: string;
  overageBehavior: string;
  overageUnitPrice?: number | null;
}

export interface ProviderLoginResponse {
  tokens: AuthTokens;
  operator: ProviderOperator;
}

/** A total in one currency. Never summed across currencies. */
export interface CurrencyTotal {
  currency: string;
  amount: number;
}

export interface CountryCount {
  countryCode: string;
  countryName: string;
  tenants: number;
}

export interface ProviderOverview {
  tenantsTotal: number;
  tenantsActive: number;
  tenantsTrialing: number;
  tenantsSuspended: number;
  trialsEndingSoon: number;
  subscriptionsPastDue: number;
  monthlyRecurring: CurrencyTotal[];
  outstanding: CurrencyTotal[];
  invoicesOverdue: number;
  paymentsAwaitingReview: number;
  ticketsOpen: number;
  ticketsAwaitingUs: number;
  ticketsBreachingToday: number;
  tenantsByCountry: CountryCount[];
}

export interface ProviderTenantRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  countryCode: string;
  currency: string;
  createdAtUtc: string;
  planName?: string | null;
  subscriptionStatus?: string | null;
  trialEndsAtUtc?: string | null;
  nextBillingAtUtc?: string | null;
  seats: number;
  usersActive: number;
  companies: number;
  outstandingAmount: number;
  openTickets: number;
}

export interface ProviderSubscriptionLine {
  description: string;
  kind: string;
  quantity: number;
  unitAmount: number;
  baseAmount: number;
  currency: string;
}

export interface ProviderEntitlementRow {
  featureKey: string;
  isEnabled: boolean;
  limit?: number | null;
  overrideLimit?: number | null;
  overrideExpiresAtUtc?: string | null;
  currentUsage: number;
}

export interface ProviderInvoiceRow {
  id: string;
  tenantId: string;
  tenantName: string;
  invoiceNumber: string;
  status: string;
  currency: string;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  dueDate?: string | null;
  issuedAtUtc?: string | null;
  isOverdue: boolean;
  declaredPayments: number;
}

export interface ProviderTenantDetail {
  summary: ProviderTenantRow;
  billingEmail?: string | null;
  billingContactName?: string | null;
  billingTaxNumber?: string | null;
  lines: ProviderSubscriptionLine[];
  recentInvoices: ProviderInvoiceRow[];
  entitlements: ProviderEntitlementRow[];
  allowSupportImpersonation: boolean;
  isReadOnly: boolean;
}

export interface ProviderPaymentRow {
  id: string;
  tenantId: string;
  tenantName: string;
  billingInvoiceId: string;
  invoiceNumber: string;
  method: string;
  amount: number;
  currency: string;
  paidOn: string;
  reference?: string | null;
  customerNote?: string | null;
  status: string;
  declaredAtUtc: string;
}


// ── Running a customer's account
//
// Everything here writes into somebody else's tenant, which is the most dangerous thing this
// console can do. Every one of them carries a reason, and the reason lands on the customer's
// own account timeline so the next person to open it can see what was agreed and by whom.

export interface AccountNoteRow {
  id: string;
  kind: string;
  subject: string;
  body: string;
  /** True only for a message actually sent to them. Everything else is ours. */
  isVisibleToCustomer: boolean;
  authorName?: string | null;
  changesJson?: string | null;
  occurredAtUtc: string;
}

export interface ProviderSubscriptionChange {
  planId?: string | null;
  seats?: number | null;
  interval?: string | null;
  status?: string | null;
  currentPeriodEndUtc?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  billingEmail?: string | null;
  billingName?: string | null;
  billingTaxNumber?: string | null;
  purchaseOrderNumber?: string | null;
  /** Required. It goes on the customer's record and stays there. */
  note: string;
}

export interface ProviderInvoiceLineInput {
  description: string;
  quantity: number;
  unitAmount: number;
}

export interface ProviderInvoiceInput {
  currency?: string | null;
  lines: ProviderInvoiceLineInput[];
  dueDate?: string | null;
  purchaseOrderNumber?: string | null;
  notes?: string | null;
  taxRate?: number | null;
  issue: boolean;
  sendEmail: boolean;
  reason: string;
}

export interface ProviderMessageInput {
  subject: string;
  body: string;
  /** billing | admins | both */
  audience: string;
  notifyInApp: boolean;
}

export interface ProviderMessageOutcome {
  delivered: number;
  skipped: number;
  recipients: string[];
  problem?: string | null;
}

export interface AiWalletView {
  creditsGranted: number;
  creditsUsed: number;
  creditsAvailable: number;
  monthlyAllowance?: number | null;
  renewsOn?: string | null;
}

// ── Reports

export interface ProviderReportDefinition {
  key: string;
  name: string;
  description: string;
  group: string;
}

export interface ProviderReportStat {
  label: string;
  value: string;
  meta?: string | null;
}

export interface ProviderReportResult {
  key: string;
  name: string;
  from: string;
  to: string;
  columns: string[];
  /** Declared by the server so a column of figures is right-aligned rather than guessed at. */
  isNumeric: boolean[];
  rows: string[][];
  stats: ProviderReportStat[];
  ranAtUtc: string;
}

// ── Tickets: what customers tell us, and what we said back
//
// One thread with two sides. A message marked internal is where an operator writes something
// true and not for the customer — so the customer's own view filters those out on the server,
// and this console shows them marked.

export interface SupportTicketRow {
  id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  category: string;
  origin: string;
  tenantId: string;
  tenantName?: string | null;
  raisedByName?: string | null;
  assignedToName?: string | null;
  createdAtUtc: string;
  lastActivityAtUtc: string;
  awaitingProviderReply: boolean;
  messageCount: number;
  /** 1-5, given when the customer closed it. */
  satisfactionRating?: number | null;
}

export interface SupportTicketMessageRow {
  id: string;
  author: string;
  authorName: string;
  body: string;
  isInternal: boolean;
  sentAtUtc: string;
}

export interface SupportTicketDetail {
  summary: SupportTicketRow;
  description: string;
  resolution?: string | null;
  firstRespondedAtUtc?: string | null;
  resolvedAtUtc?: string | null;
  raisedByEmail?: string | null;
  messages: SupportTicketMessageRow[];
}

// ── The model ClientMa itself answers with
//
// One key shared by every customer on prepaid credits. It lives in the vault and is rotated
// from the console, because a credential that needs a deployment to change is a credential
// nobody rotates.

export interface ProviderAiView {
  family: string;
  model?: string | null;
  baseUrl?: string | null;
  /** Whether a key is on file. The key itself never comes back. */
  hasApiKey: boolean;
  /** The last four characters, so a rotation can be told apart from a no-op. */
  keyHint?: string | null;
  isActive: boolean;
  verifiedAtUtc?: string | null;
  verificationError?: string | null;
  updatedByName?: string | null;
  updatedAtUtc?: string | null;
  notes?: string | null;
  supportedProviders: string[];
  /** Whether configuration still holds a key to fall back onto. */
  hasConfiguredFallback: boolean;
  /** How many customers depend on this key right now. */
  tenantsOnCredits: number;
}

export interface ProviderAiInput {
  family: string;
  model: string;
  baseUrl?: string | null;
  /** Sent once. Omit to keep the stored key. */
  apiKey?: string | null;
  isActive: boolean;
  notes?: string | null;
}

export function providerApi(api: ApiClient) {
  return {
    whoAmI: (signal?: AbortSignal) =>
      api.get<ProviderOperator>('/api/v1/provider/auth/me', { signal }),

    overview: (signal?: AbortSignal) =>
      api.get<ProviderOverview>('/api/v1/provider/overview', { signal }),

    tenants: (query: Record<string, unknown>, signal?: AbortSignal) =>
      api.get<PagedList<ProviderTenantRow>>('/api/v1/provider/tenants', { query, signal }),

    tenant: (id: string, signal?: AbortSignal) =>
      api.get<ProviderTenantDetail>(`/api/v1/provider/tenants/${id}`, { signal }),

    setTenantStatus: (id: string, status: string, reason: string) =>
      api.post<void>(`/api/v1/provider/tenants/${id}/status`, { status, reason }),

    changeSubscription: (id: string, change: ProviderSubscriptionChange) =>
      api.post<string>(`/api/v1/provider/tenants/${id}/subscription`, change),

    accountNotes: (id: string, signal?: AbortSignal) =>
      api.get<AccountNoteRow[]>(`/api/v1/provider/tenants/${id}/notes`, { signal }),

    addAccountNote: (id: string, subject: string, body: string, kind = 'General') =>
      api.post<string>(`/api/v1/provider/tenants/${id}/notes`, { subject, body, kind }),

    raiseInvoice: (id: string, input: ProviderInvoiceInput) =>
      api.post<string>(`/api/v1/provider/tenants/${id}/invoices`, input),

    messageTenant: (id: string, input: ProviderMessageInput) =>
      api.post<ProviderMessageOutcome>(`/api/v1/provider/tenants/${id}/messages`, input),

    grantAiCredits: (
      id: string,
      body: { credits?: number | null; monthlyAllowance?: number | null; reason?: string },
    ) => api.post<AiWalletView>(`/api/v1/provider/tenants/${id}/ai-credits`, body),

    tickets: (query: Record<string, unknown>, signal?: AbortSignal) =>
      api.get<PagedList<SupportTicketRow>>('/api/v1/provider/tickets', { query, signal }),

    ticket: (id: string, signal?: AbortSignal) =>
      api.get<SupportTicketDetail>(`/api/v1/provider/tickets/${id}`, { signal }),

    replyToTicket: (id: string, body: string, isInternal: boolean) =>
      api.post<void>(`/api/v1/provider/tickets/${id}/reply`, { body, isInternal }),

    updateTicket: (
      id: string,
      patch: {
        status?: string | null;
        priority?: string | null;
        assignToMe?: boolean | null;
        resolution?: string | null;
      },
    ) => api.patch<void>(`/api/v1/provider/tickets/${id}`, patch),

    aiProfile: (signal?: AbortSignal) =>
      api.get<ProviderAiView>('/api/v1/provider/ai', { signal }),

    saveAiProfile: (input: ProviderAiInput) =>
      api.put<ProviderAiView>('/api/v1/provider/ai', input),

    verifyAiProfile: () => api.post<ProviderAiView>('/api/v1/provider/ai/verify', {}),

    clearAiKey: () => api.delete<ProviderAiView>('/api/v1/provider/ai/key'),

    reportCatalogue: (signal?: AbortSignal) =>
      api.get<ProviderReportDefinition[]>('/api/v1/provider/reports', { signal }),

    runReport: (query: Record<string, unknown>, signal?: AbortSignal) =>
      api.get<ProviderReportResult>('/api/v1/provider/reports/run', { query, signal }),

    invoices: (query: Record<string, unknown>, signal?: AbortSignal) =>
      api.get<PagedList<ProviderInvoiceRow>>('/api/v1/provider/invoices', { query, signal }),

    paymentsAwaitingReview: (signal?: AbortSignal) =>
      api.get<ProviderPaymentRow[]>('/api/v1/provider/payments', { signal }),

    /** Declared | Confirmed | Rejected | all — the history behind the queue. */
    paymentsByStatus: (status: string, signal?: AbortSignal) =>
      api.get<ProviderPaymentRow[]>('/api/v1/provider/payments', {
        query: { status },
        signal,
      }),

    plans: (signal?: AbortSignal) =>
      api.get<ProviderPlanRow[]>('/api/v1/provider/plans', { signal }),

    plan: (id: string, signal?: AbortSignal) =>
      api.get<ProviderPlanDetail>(`/api/v1/provider/plans/${id}`, { signal }),

    createPlan: (input: ProviderPlanInput) =>
      api.post<{ id: string }>('/api/v1/provider/plans', input),

    updatePlan: (id: string, input: ProviderPlanInput) =>
      api.put<void>(`/api/v1/provider/plans/${id}`, input),

    setPlanActive: (id: string, isActive: boolean) =>
      api.post<void>(`/api/v1/provider/plans/${id}/status`, { isActive }),

    addPlanPrice: (id: string, input: ProviderPlanPriceInput) =>
      api.post<{ id: string }>(`/api/v1/provider/plans/${id}/prices`, input),

    updatePlanPrice: (priceId: string, input: ProviderPlanPriceInput) =>
      api.put<void>(`/api/v1/provider/plans/prices/${priceId}`, input),

    retirePlanPrice: (priceId: string) =>
      api.delete<void>(`/api/v1/provider/plans/prices/${priceId}`),

    setPlanEntitlements: (id: string, entitlements: ProviderPlanEntitlementInput[]) =>
      api.put<void>(`/api/v1/provider/plans/${id}/entitlements`, entitlements),

    renewals: (withinDays: number, signal?: AbortSignal) =>
      api.get<ProviderRenewalRow[]>('/api/v1/provider/renewals', {
        query: { withinDays },
        signal,
      }),

    reviewPayment: (id: string, confirm: boolean, note: string | null) =>
      api.post<void>(`/api/v1/provider/payments/${id}/review`, { confirm, note }),

    // ── Countries: how we take money, and where
    countries: (signal?: AbortSignal) =>
      api.get<ProviderCountryRow[]>('/api/v1/provider/countries', { signal }),

    country: (code: string, signal?: AbortSignal) =>
      api.get<ProviderCountryInput>(`/api/v1/provider/countries/${code}`, { signal }),

    saveCountry: (input: ProviderCountryInput) =>
      api.put<string>('/api/v1/provider/countries', input),

    /** The gateways this build can actually talk to, so the picker is not free text. */
    paymentProviders: (signal?: AbortSignal) =>
      api.get<string[]>('/api/v1/provider/payment-providers', { signal }),
  };
}

export interface ProviderCountryRow {
  id: string;
  countryCode: string;
  countryName: string;
  currency: string;
  isActive: boolean;
  acceptsCard: boolean;
  acceptsBankTransfer: boolean;
  acceptsCheque: boolean;
  acceptsUpi: boolean;
  supplierTaxNumber?: string | null;
  tenants: number;
  /** What is missing before this country can be billed at all. */
  gaps: string[];
}

/** The whole profile, as an operator edits it. Mirrors ProviderCountryInput on the API. */
export interface ProviderCountryInput {
  countryCode: string;
  countryName: string;
  currency: string;
  isActive: boolean;

  acceptsCard: boolean;
  cardProvider?: string | null;
  /**
   * Safe to publish — designed to be embedded in a page. The secret key it pairs with lives in
   * configuration and never in this table.
   */
  cardPublishableKey?: string | null;
  cardAccountReference?: string | null;

  /**
   * The gateway's secret key and webhook secret. Write-only.
   *
   * Never sent back — the server returns only whether one is on file. Leave them undefined to
   * keep what is stored; send an empty string to clear one deliberately.
   */
  cardSecretKey?: string | null;
  cardWebhookSecret?: string | null;

  /** Whether a secret is on file. Read-only; ignored on save. */
  hasCardSecret?: boolean;
  hasCardWebhookSecret?: boolean;

  acceptsBankTransfer: boolean;
  bankAccountName?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankBranch?: string | null;
  bankSwiftCode?: string | null;
  /** India routes a domestic transfer by IFSC; Singapore and Malaysia do not use one. */
  bankIfscCode?: string | null;
  bankAddress?: string | null;

  acceptsCheque: boolean;
  chequePayableTo?: string | null;
  chequeMailingAddress?: string | null;

  acceptsUpi: boolean;
  upiVirtualAddress?: string | null;
  upiPayeeName?: string | null;

  supplierTaxNumber?: string | null;
  taxLabel?: string | null;
  taxRate: number;
  billingEntityName?: string | null;
  billingEntityAddress?: string | null;
  paymentTermDays: number;
  paymentInstructions?: string | null;
  remittanceEmail?: string | null;
}

/**
 * Signs an operator in.
 *
 * Anonymous by necessity — there is no session yet — and pointed at the provider's own login
 * rather than the tenant one. The two are separate endpoints on purpose: a tenant credential
 * must not open this console, and the failure response here is identical whether the address is
 * unknown, the password wrong, or the account real but not an operator.
 */
export function providerLogin(
  api: ApiClient,
  email: string,
  password: string,
): Promise<ProviderLoginResponse> {
  return api.post<ProviderLoginResponse>(
    '/api/v1/provider/auth/login',
    { email, password },
    { anonymous: true },
  );
}
